import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import type {} from "@mui/x-data-grid-pro/themeAugmentation";
import skolarSansRegular from "../../SkolarSansPEWeb-Rg.woff2";
import skolarSansSemibold from "../../SkolarSansPEWeb-Sb.woff2";

declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      form: { minWidth: number };
      colors: {
        mediSecondaryGray: string;
        mediLightGray: string;
        mediDarkGray: string;
        mediDarkerGray: string;
        mediGray: string;
        mediMagenta: string;
        mediMagentaDisabled: string;
        mediRed: string;
        mediGreen: string;
        mediBlue: string;
        reallyLightGray: string;
        contentBackground: string;
        white: string;
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      form?: { minWidth?: number };
      colors?: {
        mediSecondaryGray?: string;
        mediLightGray?: string;
        mediDarkGray?: string;
        mediDarkerGray?: string;
        mediGray?: string;
        mediMagenta?: string;
        mediMagentaDisabled?: string;
        mediRed?: string;
        mediGreen?: string;
        mediBlue?: string;
        reallyLightGray?: string;
        contentBackground?: string;
        white?: string;
      };
    };
  }
}

const mediColors = {
  mediSecondaryGray: "#e9edf0",
  mediLightGray: "#d7dde2",
  mediDarkGray: "#333f48",
  mediDarkerGray: "#282e35",
  mediGray: "#48586a",
  mediMagenta: "#e6007e",
  mediMagentaDisabled: "#e6b8d1",
  mediRed: "#fe1f1f",
  mediGreen: "#8ffe16",
  mediBlue: "#91b1be",
  reallyLightGray: "#f6f8f9",
  contentBackground: "#f5f5f5",
  white: "#ffffff",
};

export const buttonMetrics = {
  fontSize: "0.9rem",
  paddingX: 20,
  paddingY: 10,
  borderWidth: 1,
};

export const customizedMaterialTheme = createTheme({
  custom: {
    form: { minWidth: 186 },
    colors: mediColors,
  },
  typography: {
    fontFamily: "SkolarSansPeSemibold",
    fontSize: 14,
  },
  palette: {
    primary: {
      light: mediColors.mediLightGray,
      main: mediColors.mediMagenta,
      dark: mediColors.mediGray,
      contrastText: mediColors.white,
    },
    error: {
      main: "#ff0000",
    },
    warning: {
      main: "#ed8b00",
    },
    success: {
      main: "#8ea40d",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: "SkolarSansPeRegular";
          src: local("SkolarSansPEWeb-Rg.woff2"), url(${skolarSansRegular}) format("woff2");
        }
        @font-face {
          font-family: "SkolarSansPeSemibold";
          src: local("SkolarSansPEWeb-Sb.woff2"), url(${skolarSansSemibold}) format("woff2");
        }
        html, body {
          width: 100%;
          height: 100%;
          display: flex;
          font-family: "SkolarSansPeSemibold" !important;
        }
        #root {
          width: 100%;
        }
        main {
          flex-grow: 1;
        }
        header, main, footer {
          flex-shrink: 0;
        }
        body h3 {
          font-size: 13pt;
          font-weight: bold;
        }
        ::-moz-selection {
          background: ${mediColors.mediMagenta};
          color: white;
        }
        ::selection {
          background: ${mediColors.mediMagenta};
          color: white;
        }
      `,
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "a&:hover": {
            color: "unset",
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: `${mediColors.mediMagenta} !important`,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginRight: 10,
          "&.MuiDataGrid-filterFormOperatorInput": {
            display: "none !important",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          display: "flex",
          padding: 0,
          "&.MuiPaper-root": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        horizontal: {
          paddingLeft: 0,
        },
        root: {
          "&:not(.Mui-completed)": {
            color: mediColors.mediLightGray,
          },
          "&.Mui-completed:hover": {
            color: `${mediColors.mediMagenta} !important`,
            cursor: "pointer",
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          "&.Mui-completed": {
            color: "inherit",
          },
          "&.Mui-completed:hover": {
            color: "inherit",
          },
          width: "12px",
          height: "12px",
          text: {
            display: "none",
          },
          color: "inherit",
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          "&.Mui-completed": {
            color: "inherit",
          },
          "&.Mui-completed:hover": {
            color: "inherit",
          },
        },
        label: {
          "&.Mui-active": {
            color: `${mediColors.mediMagenta} !important`,
          },
          "&.Mui-completed": {
            color: "inherit",
          },
          "&.Mui-completed:hover": {
            color: "inherit",
          },
        },
        labelContainer: {
          color: "inherit",
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          paddingRight: 8,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        container: {
          background: "white",
          marginBottom: 8,
        },
        gutters: {
          marginBottom: 8,
          background: "white",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: mediColors.mediMagenta,
          color: "white",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: mediColors.mediGray,
          textTransform: "none",
          "&.MuiButtonBase-root": {
            color: "white",
            opacity: 1,
            borderBottomColor: mediColors.mediMagenta,
            borderWidth: 0,
            border: "solid",
            borderBottomWidth: 2,
          },
          "&.Mui-selected": {
            color: mediColors.mediGray,
            backgroundColor: "transparent",
            borderColor: mediColors.mediMagenta,
            borderWidth: 2,
            border: "solid",
            borderBottomWidth: 0,
          },
          "&.MuiTab-textColorInherit.Mui-disabled": {
            backgroundColor: "transparent",
            opacity: 1,
            minWidth: "100%",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: "none",
          fontSize: buttonMetrics.fontSize,
          padding: `${buttonMetrics.paddingY}px ${buttonMetrics.paddingX}px`,
        },
        outlined: {
          backgroundColor: "white",
          borderColor: mediColors.mediLightGray,
          color: mediColors.mediGray,
          "&:hover, &:focus, &:active": {
            borderColor: mediColors.mediMagenta,
            backgroundColor: "white",
          },
          "&.Mui-disabled": {
            color: mediColors.mediLightGray,
            borderColor: mediColors.mediLightGray,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          "&.MuiDialogContent-root": {
            whiteSpace: "pre-wrap",
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "&.MuiDataGrid-root .MuiDataGrid-row.whiteBackground": {
            backgroundColor: "white",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: `${mediColors.mediSecondaryGray} !important`,
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: mediColors.mediSecondaryGray,
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: mediColors.reallyLightGray,
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: mediColors.white,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: mediColors.mediSecondaryGray,
          },
        },
      },
    },
  },
});
